<template>
  <IdentityShell>
    <template #masthead>
      <IdentityWelcomeMasthead
        :kicker="$t('auth.user.directoryVerifyPage.kicker', 'Verify')"
        :headline="
          $t('auth.user.directoryVerifyPage.headline', 'Confirm it\'s you.')
        "
        :subtitle="
          $t(
            'auth.user.directoryVerifyPage.subtitle',
            'Almost there. Enter the 6-digit code we just emailed you to confirm it\'s really you.'
          )
        "
      />
    </template>

    <template #title>{{
      $t('auth.user.directoryVerifyPage.checkYourEmail', 'Check your email')
    }}</template>
    <template #lede>
      {{
        $t(
          'auth.user.directoryVerifyPage.codeSentTo',
          'We sent a 6-digit code to'
        )
      }}
      <strong>{{ displayEmail }}</strong
      >{{
        $t(
          'auth.user.directoryVerifyPage.codeExpires',
          '. It expires in 15 minutes.'
        )
      }}
    </template>

    <AlertDanger v-if="firstError" show class="mb-3">
      {{ firstError }}
    </AlertDanger>

    <FormBase @submit.prevent="onSubmit">
      <FormGroupBase
        class="mb-3"
        :label="$t('auth.user.directoryVerifyPage.codeLabel', '6-digit code')"
        :state="fieldState('code')"
        :invalid-feedback="fieldError('code')"
      >
        <InputText
          v-model="code"
          inputmode="numeric"
          autocomplete="one-time-code"
          autofocus
          maxlength="6"
          :placeholder="'123456'"
          :state="fieldState('code')"
          :ignore1-password="true"
          class="directory-verify__code"
        />
      </FormGroupBase>

      <ButtonPrimary type="submit" block :disabled="submitting">
        {{
          submitting
            ? $t('auth.user.directoryVerifyPage.verifying', 'Verifying…')
            : $t('auth.user.directoryVerifyPage.continue', 'Continue')
        }}
      </ButtonPrimary>
    </FormBase>

    <p class="directory-verify__resend">
      {{ $t('auth.user.directoryVerifyPage.didntGetIt', "Didn't get it?") }}
      <RouterLink to="/login">{{
        $t('auth.user.directoryVerifyPage.sendAnotherCode', 'Send another code')
      }}</RouterLink>
    </p>
  </IdentityShell>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import { FormBase, FormGroupBase } from '@/components/base/Form';
import {
  IdentityShell,
  IdentityWelcomeMasthead,
} from '@/components/feature/Identity';
import { AlertDanger } from '@/components/ui/Alert';
import { ButtonPrimary } from '@/components/ui/Button';
import { InputText } from '@/components/ui/Form';
import { useIdentityBoot } from '@/composables/useIdentityBoot';
import { useIdentityForm } from '@/composables/useIdentityForm';

const route = useRoute();
const boot = useIdentityBoot();

const bootEmail =
  ((boot.data as { email?: string } | undefined)?.email ?? '') || '';
const queryEmail =
  typeof route.query.email === 'string' ? route.query.email : '';
const initialEmail = bootEmail || queryEmail;

const email = ref(initialEmail);
const code = ref('');

const displayEmail = computed(() => email.value || 'that address');

const { firstError, fieldState, fieldError, submitting, submit } =
  useIdentityForm({ url: '/login/verify' });

async function onSubmit() {
  await submit({ email: email.value, code: code.value });
}
</script>

<style lang="scss" scoped>
.directory-verify__code {
  font-family: 'Inter', system-ui, sans-serif;
  font-feature-settings:
    'tnum' 1,
    'lnum' 1;
  font-size: 1.5rem;
  letter-spacing: 0.32em;
  text-align: center;
  font-weight: 600;
}

.directory-verify__resend {
  margin-top: 1.5rem;
  margin-bottom: 0;
  text-align: center;
  font-size: 0.875rem;
  color: #5b6372;

  a {
    color: #1984c4;
    font-weight: 500;
    text-decoration: none;
    border-bottom: 1px solid transparent;

    &:hover {
      border-bottom-color: currentColor;
    }
  }
}
</style>
